import { Schema } from "prosemirror-model";
import { Command, EditorState } from "prosemirror-state";
import { useCallback, useMemo } from "react";
import { hasNodes } from "../schema";
import { CallbackWithSchemaResult, useExtendWithSchema } from "./useExtendWithSchema";

export type EmojiToolHandlers = {
	isSupportedBySchema: true;
	insertEmoji(emojiUnicode: string): void;
} | {
	isSupportedBySchema: false;
};

export function useEmojiTool(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	focus?: () => void
): EmojiToolHandlers {
	const insertEmojiHandler: CallbackWithSchemaResult<
		[emojiUnicode: string],
		void
	> = useExtendWithSchema(
		editorState.schema,
		schema => hasNodes(schema, ["text"]),
		useCallback(
			(_schema: Schema<"text", never>, emojiUnicode: string): void => {
				applyCommand(
					(state, dispatch): boolean => {
						if (dispatch) {
							dispatch(state.tr.insertText(emojiUnicode));
						}

						return true;
					}
				);

				focus?.();
			},
			[
				applyCommand,
				focus
			]
		)
	);

	return useMemo(
		(): EmojiToolHandlers => {
			if (!insertEmojiHandler.isSupportedBySchema) {
				return {
					isSupportedBySchema: false
				};
			}

			return {
				isSupportedBySchema: true,
				insertEmoji: insertEmojiHandler
			};
		},
		[insertEmojiHandler]
	);
}
